/* ================================================================
   BRITTEN CUSTOM: Dress finder — "Find your veil by your dress"
   ================================================================
   Added:       September 2026
   Author:      Claude / Matthew
   Location:    assets/britten-dress-finder.js
   Called from: snippets/britten-dress-finder.liquid (templates/page.britten-
                dress-finder.liquid), loaded as an ordinary deferred script
                AFTER assets/britten-dress-match-core.js.

   PURPOSE:
   All behaviour of the Dress Finder page. A bride uploads a photo of her
   dress, or describes it; this file shows the veils real Britten brides
   chose with dresses like hers, then those brides. It owns the interface
   only: reading the photo, loading the bride index and scorer, ranking and
   remembering the dress are all window.BrittenDressMatchCore (the same
   engine the Real Brides "Brides in a dress like yours" block uses).

   HOW IT WORKS:
   1. States (root data-bdf-state): start (upload box) -> reading (photo in
      flight, or a remembered dress loading) -> results. `unavailable` swaps
      the upload box for a calm line and keeps the rest of the page.
   2. Upload: the whole box is a <label>, so a click opens the file picker;
      drag and drop is wired here. core.readPhoto() returns tags or a
      reason; every reason gets a kind message (the dress-match copy). A
      slow answer from an earlier photo can never beat a newer one (the core
      keeps the generation; this file keeps its own for the page).
   3. Describe picker: Silhouette / Neckline / Sleeves / Fabric options are
      real dress vocabulary values, one for one (see PICKER below). Find
      stays off, with a short hint under it, until the details chosen can
      reach the scorer's minimum (see ENOUGH DETAIL below).
   4. Results: tags -> core.rank(). Veils are tallied here from the ranked
      list (matched-bride count per veil, top four); each veil's image is the
      best-scoring matched photo showing that veil. Brides: those with a
      wedding page first (linked cards), then album/review brides (unlinked
      cards), closest first within each group (core.closestFirst order).
   5. Editing a chip (tap it) re-ranks at once; no new network call.
   6. The dress (attributes only) is remembered with core.remember() so a
      bride can come back from a product page; on load, core.recall() shows
      the results straight away under "Your last dress".
   7. Veil prices ("from £45") are fetched AFTER the results from
      /products/<handle>.js (four small requests, cached), formatted with
      Intl.NumberFormat in Shopify.currency.active (price_min is in minor
      units). Any failure leaves the line empty; nothing else changes.
   8. Every string comes from window.BrittenI18n (britten.js.rb.df_* plus the
      existing britten.js.rb.dm_* keys), never a bare literal.

   PICKER:
   Every button is a real vocabulary value. The Sleeves row carries the
   strap choices: "Strapless" is sleeves = none, "Straps" is
   sleeves = thin_straps, so a strappy sleeveless dress is not recorded as
   strapless. The chips' correction popover uses the same words (the
   vocabulary's "Thin straps" reads "Straps" there too).

   ENOUGH DETAIL:
   The scorer only ranks a bride when the details it compares reach a
   minimum weight (per-field weights from the index; the minimum from the
   index or the scorer's own default). One detail such as A-line cannot reach
   it, so it used to end in "we couldn't find a match" straight after she had
   described her dress. enoughDetail() does not keep a copy of the weights or
   the minimum: it asks the loaded scorer to rank a made-up bride whose dress
   is exactly this one, and she comes back only when the details can reach the
   minimum. When they cannot, the picker keeps Find off and the results page
   (photo path or edited chips) shows "add one more detail" naming the groups
   that would do it, never "no match". "No match" is kept for a real empty
   result with enough detail. The index has to be loaded to know; until it is,
   the picker behaves as before and the results page makes the same check.

   REQUIREMENTS:
   - window.BrittenDressMatchCore (assets/britten-dress-match-core.js) and
     window.BrittenI18n must be on the page. If the core is missing the
     upload box is replaced by the calm "isn't available" line.
   - Markup from snippets/britten-dress-finder.liquid (data-bdf-* hooks).

   THEME DEPENDENCY:
   assets/britten-dress-finder.css (.bdf-*) and assets/britten-archive-cards.css
   (.brac-* bride card). Analytics through core.emit(): finder_upload,
   finder_describe, finder_tags_ready, finder_results, finder_chip_change,
   finder_shop_click, finder_worn_click, finder_bride_click, finder_see_all,
   finder_reset (the 'bdm:event' event and Shopify.analytics
   'dress_match_<name>').

   CHANGELOG:
   - 15 Oct 2026: crops aim at the bride (fp, marketing_131); no fp = old crop. The veil
     cards and the bride cards set object-position from the photo's fp.
   - 29 Sep 2026: "Shop the veil" writes the cart's rb_source = 'dress_finder' (first touch
     wins; assets/britten-rb-session.js), and every finder event now carries rb_sid when
     analytics consent allows one (added by core.emit).
   - 29 Sep 2026: "See it worn" keeps her dress. The veil card's main link is now "See it
     with dresses like yours" -> /pages/real-brides?dress=1&product=<handle> (the archive
     shows the matched brides who wore that veil), with a small "See all <veil> brides"
     link under it to the plain product filter (?product=<handle>). Both still fire
     finder_worn_click; its payload gains `link` ('dress' or 'all'). Both links are
     hidden on mobile, as "See it worn" was.
   - 29 Sep 2026: (1) too little detail no longer ends in "no match": Find stays off
     with a hint under it, and the results page says "add one more detail", naming
     the groups that would reach the scorer's minimum (asked of the scorer, no copy
     of its weights). (2) Separates removed from the picker. (3) "Strapless" is now
     a Sleeves choice (none) beside a new "Straps" (thin_straps), and the correction
     chips and popover use the same words.
   - 2026-09-29: initial build.
   ================================================================ */
(function () {
  'use strict';

  // === CONFIGURATION ===
  var FEATURE_ENABLED = true;
  var DEBUG_MODE = false;

  function log(msg) {
    if (DEBUG_MODE && window.console) console.log('[Britten Dress Finder] ' + msg);
  }

  var root = document.getElementById('bdf');
  if (!root) return;
  if (!FEATURE_ENABLED) { root.classList.add('bdf--off'); return; }

  var TOP_VEILS = 4;
  var GRID_SIZE = 8;
  var READING_INTERVAL_MS = 1400;

  // Same field order the scoring engine displays known values in.
  var DRESS_FIELDS = ['silhouette', 'neckline', 'sleeves', 'sleeve_fabric',
    'lace', 'back', 'buttons', 'train', 'fabric', 'embellishment', 'colour', 'slit'];
  // Fallbacks for an index built before it carried chip_secondary /
  // chip_hide_values (same as the Real Brides block).
  var DEFAULT_PRIMARY_FIELDS = ['silhouette', 'neckline', 'sleeves', 'lace', 'back', 'train', 'fabric'];
  var DEFAULT_HIDE_VALUES = {
    buttons: ['no'], slit: ['no'], embellishment: ['none'], lace: ['none'],
    train: ['none'], sleeve_fabric: ['na']
  };
  // Unknown ones of these show as a dashed "+ Neckline" chip, so a bride can add them.
  var ASK_FIELDS = ['silhouette', 'neckline', 'sleeves'];
  // The describe picker's rows, top to bottom.
  var PICKER_FIELDS = ['silhouette', 'neckline', 'sleeves', 'fabric'];

  // ---- i18n (window.BrittenI18n; see snippets/britten-i18n-js.liquid) ----
  var i18n = window.BrittenI18n;
  var safeI18n = {
    t: function (key, vars) { return i18n ? i18n.t(key, vars) : ''; },
    n: function (key, count, vars) { return i18n ? i18n.n(key, count, vars) : ''; }
  };

  var FIELD_LABEL = {
    silhouette: safeI18n.t('js.rb.dm_field_silhouette'),
    neckline: safeI18n.t('js.rb.dm_field_neckline'),
    sleeves: safeI18n.t('js.rb.dm_field_sleeves'),
    sleeve_fabric: safeI18n.t('js.rb.dm_field_sleeve_fabric'),
    lace: safeI18n.t('js.rb.dm_field_lace'),
    back: safeI18n.t('js.rb.dm_field_back'),
    buttons: safeI18n.t('js.rb.dm_field_buttons'),
    train: safeI18n.t('js.rb.dm_field_train'),
    fabric: safeI18n.t('js.rb.dm_field_fabric'),
    embellishment: safeI18n.t('js.rb.dm_field_embellishment'),
    colour: safeI18n.t('js.rb.dm_field_colour'),
    slit: safeI18n.t('js.rb.dm_field_slit'),
    extras: safeI18n.t('js.rb.dm_field_extras')
  };
  // "Add a neckline or a sleeve style ...": what the add-a-detail hint calls each group.
  var NEED_PHRASE = {
    silhouette: safeI18n.t('js.rb.df_need_silhouette'),
    neckline: safeI18n.t('js.rb.df_need_neckline'),
    sleeves: safeI18n.t('js.rb.df_need_sleeves'),
    fabric: safeI18n.t('js.rb.df_need_fabric')
  };
  // The vocabulary calls thin_straps "Thin straps"; the picker says "Straps", so the chips do too.
  var STRAPS_LABEL = safeI18n.t('rb.df_opt_straps');
  var BAND_LABEL = {
    very_close: safeI18n.t('js.rb.dm_band_very_close'),
    close: safeI18n.t('js.rb.dm_band_close'),
    similar: safeI18n.t('js.rb.dm_band_similar')
  };
  var READING_LINES = [
    safeI18n.t('js.rb.dm_reading_1'),
    safeI18n.t('js.rb.dm_reading_2'),
    safeI18n.t('js.rb.dm_reading_3')
  ];

  // ---- DOM ----
  function q(sel) { return root.querySelector(sel); }
  var startEl = q('[data-bdf-start]');
  var uiEl = q('[data-bdf-ui]');
  var noticeEl = q('[data-bdf-notice]');
  var dropEl = q('[data-bdf-drop]');
  var fileEl = q('[data-bdf-file]');
  var describeBtn = q('[data-bdf-describe]');
  var pickerEl = q('[data-bdf-picker]');
  var optBtns = Array.prototype.slice.call(root.querySelectorAll('.bdf-opt'));
  var findBtn = q('[data-bdf-find]');
  var hintEl = q('[data-bdf-hint]');
  var howEl = q('[data-bdf-how]');
  var recentEl = q('[data-bdf-recent]');
  var resultsEl = q('[data-bdf-results]');
  var barEl = q('[data-bdf-bar]');
  var thumbEl = q('[data-bdf-thumb]');
  var labelEl = q('[data-bdf-label]');
  var statusEl = q('[data-bdf-status]');
  var chipsEl = q('[data-bdf-chips]');
  var chipListEl = q('[data-bdf-chip-list]');
  var resetBtn = q('[data-bdf-reset]');
  var popEl = q('[data-bdf-pop]');
  var noteEl = q('[data-bdf-note]');
  var veilsEl = q('[data-bdf-veils]');
  var veilsMetaEl = q('[data-bdf-veils-meta]');
  var veilRowEl = q('[data-bdf-veil-row]');
  var bridesEl = q('[data-bdf-brides]');
  var bridesMetaEl = q('[data-bdf-brides-meta]');
  var brideGridEl = q('[data-bdf-bride-grid]');
  var seeAllEl = q('[data-bdf-seeall]');
  if (!startEl || !uiEl || !dropEl || !fileEl || !resultsEl || !barEl || !popEl) return;

  var ROOT_PREFIX = (root.getAttribute('data-root') || '').replace(/\/+$/, '');
  var DEFAULT_LABEL = labelEl ? labelEl.textContent : '';

  // ---- state ----
  var S = {
    state: 'start',
    engine: null,          // { index, api } once loaded
    enginePromise: null,
    tags: null,            // the dress, as the chips edit it
    ranked: null,
    lowCoverage: false,
    recalled: false,
    gen: 0,                // bumped by every new dress and by Reset
    previewUrl: '',        // an object URL to revoke, or ''
    timer: null,
    popField: null,
    unavailable: false
  };
  var pickerSel = {};
  var priceCache = {};

  var core = null;

  // ---- small helpers ----
  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null && text !== '') node.textContent = text;
    return node;
  }

  function cdnUrl(u, w) {
    if (!u) return u;
    if (!/^https?:\/\/cdn\.shopify\.com\//.test(u)) return u;
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  // Archive number as the archive prints it: № + Roman numerals.
  function toRoman(n) {
    var vals = [[1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'],
      [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']];
    var out = '';
    n = Math.floor(Number(n) || 0);
    if (n < 1 || n > 3999) return '';
    for (var i = 0; i < vals.length; i++) {
      while (n >= vals[i][0]) { out += vals[i][1]; n -= vals[i][0]; }
    }
    return out;
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function hasKeys(obj) { return !!obj && Object.keys(obj).length > 0; }

  // One word for one thing, in the picker and in the correction chips and popover.
  function optionLabel(field, value, fallback) {
    if (field === 'sleeves' && value === 'thin_straps' && STRAPS_LABEL) return STRAPS_LABEL;
    return fallback;
  }

  function veilFor(id) {
    var v = S.engine && S.engine.index && S.engine.index.veils;
    return (v && id != null && v[id]) || null;
  }

  function handleOf(url) {
    var m = /\/products\/([^\/?#]+)/.exec(url || '');
    return m ? m[1] : '';
  }

  // A wedding page: the index's `href`, which is only there when the wedding
  // page is published. (`s`, the slug, is on every published wedding whether
  // or not it has a page, so it is not a link.) Album and review brides have none.
  function pageUrlOf(bride) {
    return (bride && bride.href) || '';
  }

  function idle(fn) {
    if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 1);
  }

  // ================================================================
  // State + view
  // ================================================================
  function setState(name) {
    S.state = name;
    root.setAttribute('data-bdf-state', name);
    var inResults = name === 'reading' || name === 'results';
    startEl.hidden = inResults;
    howEl.hidden = inResults;
    if (recentEl) recentEl.hidden = inResults;
    resultsEl.hidden = !inResults;
    barEl.classList.toggle('bdf-bar--reading', name === 'reading');
  }

  function showNotice(msg) {
    noticeEl.textContent = msg || '';
    noticeEl.hidden = !msg;
  }

  function setThumb(url) {
    var ok = !!url && /^(blob:|data:image\/)/.test(url);
    thumbEl.hidden = !ok;
    thumbEl.style.backgroundImage = ok ? 'url("' + url + '")' : '';
  }

  function releasePreview() {
    if (S.previewUrl) core.revoke(S.previewUrl);
    S.previewUrl = '';
  }

  function startReading() {
    stopReading();
    var i = 0;
    statusEl.textContent = READING_LINES[0] || '';
    S.timer = setInterval(function () {
      i = (i + 1) % READING_LINES.length;
      statusEl.textContent = READING_LINES[i] || '';
    }, READING_INTERVAL_MS);
  }

  function stopReading() {
    if (S.timer) { clearInterval(S.timer); S.timer = null; }
  }

  // Empties the results area. `keepBar` leaves the bar's chips for renderBar to redo.
  function clearResultsDom(keepBar) {
    if (!keepBar) {
      chipListEl.textContent = '';
      chipsEl.hidden = true;
    }
    noteEl.hidden = true;
    noteEl.textContent = '';
    veilsEl.hidden = true;
    veilRowEl.textContent = '';
    veilsMetaEl.textContent = '';
    bridesEl.hidden = true;
    brideGridEl.textContent = '';
    bridesMetaEl.textContent = '';
  }

  function showStart(msg, openPicker) {
    stopReading();
    closePop();
    setState('start');
    showNotice(msg || '');
    if (openPicker) setPicker(true);
  }

  // The engine (or the whole index) cannot be used: swap the upload box for a
  // calm line, keep the header, the steps and the strip. Never throws.
  function markUnavailable(err) {
    try {
      S.unavailable = true;
      S.gen += 1;
      stopReading();
      closePop();
      releasePreview();
      setThumb('');
      var code = err && err.code;
      uiEl.hidden = true;
      setState('start');
      showNotice(safeI18n.t(code === 'no_score' ? 'js.rb.dm_state_engine_unavailable' : 'js.rb.dm_state_no_brides_yet'));
    } catch (e) { /* never throw out of a failure path */ }
  }

  // ================================================================
  // Engine
  // ================================================================
  function ensureEngine() {
    if (!S.enginePromise) {
      S.enginePromise = core.loadEngine().then(function (engine) {
        S.engine = engine;
        syncPicker(); // the picker can now tell whether its details are enough
        return engine;
      });
      S.enginePromise.catch(function () { /* handled where the engine is needed */ });
    }
    return S.enginePromise;
  }

  // ================================================================
  // Upload
  // ================================================================
  function handleFile(file) {
    if (!file || S.unavailable) return;
    S.gen += 1;
    var mine = S.gen;
    closePop();
    releasePreview();
    S.recalled = false;
    S.tags = null;
    S.ranked = null;
    S.lowCoverage = false;
    showNotice('');
    clearResultsDom();
    core.emit('finder_upload', { size: file.size, type: file.type || '' });
    S.previewUrl = core.previewUrl(file);
    setThumb(S.previewUrl);
    labelEl.textContent = DEFAULT_LABEL;
    setState('reading');
    startReading();
    ensureEngine();
    core.readPhoto(file, {
      // A browser that cannot show the file (HEIC) gets the downscaled JPEG.
      onPreview: function (dataUrl) { if (mine === S.gen && !S.previewUrl) setThumb(dataUrl); }
    }).then(function (res) {
      if (mine !== S.gen) return;
      onRead(res, mine);
    });
  }

  function onRead(res, mine) {
    stopReading();
    if (!res || res.ok !== true) {
      onReadFailed(res && res.reason);
      return;
    }
    S.tags = res.tags || {};
    S.lowCoverage = res.coverage === 'none' || res.coverage === 'detail';
    core.emit('finder_tags_ready', { fields: Object.keys(S.tags).length });
    showResults(mine);
  }

  function onReadFailed(reason) {
    if (reason === 'superseded') return;
    releasePreview();
    setThumb('');
    if (reason === 'not_a_dress' || reason === 'unclear') { showStart(safeI18n.t('js.rb.dm_state_not_dress')); return; }
    if (reason === 'too_large') { showStart(safeI18n.t('js.rb.dm_state_too_large')); return; }
    if (reason === 'bad_image') { showStart(safeI18n.t('js.rb.dm_state_bad_image')); return; }
    // busy | paused | unavailable | timeout: never a dead end, open the picker.
    showStart(safeI18n.t('js.rb.dm_state_resting'), true);
  }

  function showResults(mine) {
    ensureEngine().then(function () {
      if (mine !== S.gen) return;
      render();
    }, function (err) {
      if (mine !== S.gen) return;
      markUnavailable(err);
    });
  }

  // ================================================================
  // Enough detail to match on? (see ENOUGH DETAIL in the header)
  // ================================================================
  // `nt` is a normalised dress. True when its known details can reach the
  // scorer's minimum. The scorer answers, so its weights and minimum are the
  // only copy: a made-up bride with exactly this dress is ranked, and she is
  // returned only if the details compared reach the minimum. Cannot tell (index
  // not loaded, scorer missing the call) counts as enough, so the match runs.
  function enoughDetail(nt) {
    var eng = S.engine;
    if (!eng || !eng.api || !eng.index || typeof eng.api.rank !== 'function') return true;
    if (!hasKeys(nt)) return false;
    try {
      var probe = Object.assign({}, eng.index, { brides: [{ id: 'bdf-probe', dress: nt }] });
      return eng.api.rank(nt, probe, { includeWeak: true }).length > 0;
    } catch (e) { return true; }
  }

  // Of `candidates` (fields she has not given), the ones that would get the dress
  // over the line if she added one, heaviest first (weights from the index).
  function helpfulFields(nt, candidates) {
    var index = S.engine.index;
    var vocab = index.vocab || {};
    var weights = index.weights || {};
    return candidates.map(function (f, i) { return { f: f, i: i }; }).filter(function (c) {
      if (nt[c.f] !== undefined) return false;
      var value = Object.keys(vocab[c.f] || {}).filter(function (v) { return v !== 'unknown'; })[0] || 'x';
      var probe = Object.assign({}, nt);
      probe[c.f] = value;
      return enoughDetail(probe);
    }).sort(function (a, b) {
      return ((weights[b.f] || 0) - (weights[a.f] || 0)) || (a.i - b.i);
    }).map(function (c) { return c.f; });
  }

  // "Lovely. Add a neckline or a sleeve style so we can find brides with a dress like yours."
  function moreDetailNote(nt, candidates) {
    var names = helpfulFields(nt, candidates).slice(0, 2).map(function (f) { return NEED_PHRASE[f]; }).filter(Boolean);
    if (!names.length) return safeI18n.t('js.rb.df_more_detail_generic');
    var groups = names.length === 2
      ? safeI18n.t('js.rb.df_need_pair', { first: names[0], second: names[1] })
      : names[0];
    return safeI18n.t('js.rb.df_more_detail', { groups: groups });
  }

  // ================================================================
  // Describe picker
  // ================================================================
  function setPicker(open) {
    pickerEl.hidden = !open;
    describeBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  // PICKER (see the header): the rows are already real dress tags.
  function pickerTags() {
    var t = {};
    PICKER_FIELDS.forEach(function (f) { if (pickerSel[f]) t[f] = pickerSel[f]; });
    return t;
  }

  // Find is on when something is chosen and, once the index is loaded, when
  // what is chosen can reach the scorer's minimum. Otherwise the hint says what to add.
  function syncPicker() {
    optBtns.forEach(function (b) {
      var on = pickerSel[b.getAttribute('data-bdf-field')] === b.getAttribute('data-bdf-value');
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    var tags = pickerTags();
    var ready = hasKeys(tags);
    var hint = '';
    if (ready && S.engine) {
      var nt = core.normalise(tags);
      if (!enoughDetail(nt)) {
        ready = false;
        hint = moreDetailNote(nt, PICKER_FIELDS);
      }
    }
    findBtn.disabled = !ready;
    if (hintEl) {
      hintEl.textContent = hint;
      hintEl.hidden = !hint;
    }
  }

  function findFromPicker() {
    var tags = pickerTags();
    if (!hasKeys(tags) || S.unavailable) return;
    S.gen += 1;
    var mine = S.gen;
    core.cancel();
    releasePreview();
    setThumb('');
    S.tags = tags;
    S.ranked = null;
    S.recalled = false;
    S.lowCoverage = false;
    showNotice('');
    clearResultsDom();
    labelEl.textContent = DEFAULT_LABEL;
    core.emit('finder_describe', { fields: Object.keys(tags).length });
    setState('reading');
    startReading();
    showResults(mine);
  }

  // ================================================================
  // Your dress bar: editable chips
  // ================================================================
  function primaryFields() {
    var index = S.engine && S.engine.index;
    var secondary = index && Array.isArray(index.chip_secondary) ? index.chip_secondary : null;
    if (!secondary) return DEFAULT_PRIMARY_FIELDS;
    return DRESS_FIELDS.filter(function (f) { return secondary.indexOf(f) === -1; });
  }

  function isHiddenValue(field, value) {
    var index = S.engine && S.engine.index;
    var table = (index && index.chip_hide_values) || DEFAULT_HIDE_VALUES;
    var hidden = table[field];
    return !!hidden && hidden.indexOf(value) > -1;
  }

  function buildChip(field, label, known) {
    var b = el('button', known ? 'bdf-chip' : 'bdf-chip bdf-chip--add');
    b.type = 'button';
    b.setAttribute('data-field', field);
    var fLabel = FIELD_LABEL[field] || field;
    if (known) {
      b.textContent = label;
      b.setAttribute('aria-label', safeI18n.t('js.rb.dm_edit_field_aria', { field: fLabel }));
    } else {
      b.textContent = safeI18n.t('js.rb.dm_add_field', { field: fLabel });
    }
    b.addEventListener('click', function () { openPop(field, b); });
    return b;
  }

  function renderBar() {
    chipListEl.textContent = '';
    var known = {};
    (core.describe(S.tags) || []).forEach(function (d) {
      if (d.field !== 'extras') known[d.field] = d;
    });
    primaryFields().forEach(function (field) {
      var d = known[field];
      if (d) {
        if (isHiddenValue(field, d.value)) return;
        chipListEl.appendChild(buildChip(field, optionLabel(field, d.value, d.label), true));
      } else if (ASK_FIELDS.indexOf(field) > -1) {
        chipListEl.appendChild(buildChip(field, null, false));
      }
    });
    chipsEl.hidden = false;
    labelEl.textContent = S.recalled ? safeI18n.t('js.rb.df_last_dress') : DEFAULT_LABEL;
  }

  function focusChip(field) {
    var c = chipListEl.querySelector('[data-field="' + field + '"]');
    if (c) c.focus();
  }

  // ---- chip popover ----
  function popKeydown(e) {
    if (e.key === 'Escape') {
      var f = S.popField;
      closePop();
      if (f) focusChip(f);
      return;
    }
    if (e.key === 'Tab') {
      var items = popEl.querySelectorAll('button');
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  function popOutside(e) {
    if (popEl.hidden) return;
    if (!popEl.contains(e.target)) closePop();
  }
  function closePop() {
    popEl.hidden = true;
    popEl.textContent = '';
    S.popField = null;
    document.removeEventListener('keydown', popKeydown);
    document.removeEventListener('pointerdown', popOutside, true);
  }

  function openPop(field, anchor) {
    closePop();
    S.popField = field;
    var index = S.engine && S.engine.index;
    var values = (index && index.vocab && index.vocab[field]) || {};
    var current = S.tags ? S.tags[field] : undefined;

    var head = el('div', 'bdf-pop__head');
    head.appendChild(el('p', 'bdf-pop__title', FIELD_LABEL[field] || field));
    var close = el('button', 'bdf-pop__close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', safeI18n.t('js.rb.dm_pop_close_aria'));
    close.addEventListener('click', function () { closePop(); focusChip(field); });
    head.appendChild(close);
    popEl.appendChild(head);

    var opts = el('div', 'bdf-pop__opts');
    Object.keys(values).forEach(function (val) {
      var o = el('button', 'bdf-pop__opt' + (current === val ? ' bdf-pop__opt--on' : ''), optionLabel(field, val, values[val]));
      o.type = 'button';
      o.addEventListener('click', function () {
        S.tags[field] = val;
        afterEdit(field);
      });
      opts.appendChild(o);
    });
    var notSure = el('button', 'bdf-pop__opt bdf-pop__opt--none', safeI18n.t('js.rb.dm_not_sure'));
    notSure.type = 'button';
    notSure.addEventListener('click', function () {
      delete S.tags[field];
      afterEdit(field);
    });
    opts.appendChild(notSure);
    popEl.appendChild(opts);

    popEl.hidden = false;
    var barRect = barEl.getBoundingClientRect();
    var aRect = anchor.getBoundingClientRect();
    var left = aRect.left - barRect.left;
    var maxLeft = barRect.width - popEl.offsetWidth;
    if (left > maxLeft) left = Math.max(0, maxLeft);
    popEl.style.top = (aRect.bottom - barRect.top + 8) + 'px';
    popEl.style.left = left + 'px';
    var startBtn = popEl.querySelector('.bdf-pop__opt--on') || popEl.querySelector('.bdf-pop__opt');
    if (startBtn) startBtn.focus();
    document.addEventListener('keydown', popKeydown);
    document.addEventListener('pointerdown', popOutside, true);
  }

  function afterEdit(field) {
    closePop();
    render(true);
    focusChip(field);
    core.emit('finder_chip_change', { field: field });
  }

  // ================================================================
  // Results
  // ================================================================
  function showNote(msg) {
    noteEl.textContent = msg || '';
    noteEl.hidden = !msg;
  }

  // `isEdit` is true when a chip was changed: same dress, so no new "results" event.
  function render(isEdit) {
    stopReading();
    var tags = S.tags || {};
    clearResultsDom(true);
    setState('results');
    statusEl.textContent = safeI18n.t('js.rb.dm_tags_ready');
    renderBar();

    var nt = core.normalise(tags);
    // Nothing to match on, or nothing matched. From a photo we could barely see,
    // the useful thing to say is the low-coverage line (add details with the chips).
    var emptyNote = safeI18n.t(S.lowCoverage ? 'js.rb.dm_state_low_coverage' : 'js.rb.dm_state_no_results');
    if (!hasKeys(nt)) {
      S.ranked = null;
      showNote(emptyNote);
      return;
    }
    // Some detail, but too little to compare on (a photo we barely read, or a
    // chip cleared): say what to add. "No match" is for a real empty result.
    if (!enoughDetail(nt)) {
      S.ranked = null;
      showNote(moreDetailNote(nt, ASK_FIELDS));
      return;
    }

    var ranked = core.rank(nt, {});
    var weak = false;
    if (!ranked.length) {
      ranked = core.rank(nt, { includeWeak: true });
      weak = true;
    }
    S.ranked = ranked;
    if (!ranked.length) {
      showNote(emptyNote);
      return;
    }

    if (weak) showNote(safeI18n.t('js.rb.dm_state_no_close_matches'));
    else if (S.lowCoverage) showNote(safeI18n.t('js.rb.dm_state_low_coverage'));

    core.remember(tags);
    if (!isEdit) core.emit('finder_results', { brides: ranked.length, weak: weak });

    if (!weak) renderVeils(tallyVeils(ranked), ranked.length);
    renderBrides(ranked);
    scrollBarIntoView();
  }

  function scrollBarIntoView() {
    if (typeof barEl.scrollIntoView !== 'function') return;
    var r = barEl.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (r.top >= 0 && r.top < vh * 0.6) return;
    barEl.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }

  // ---- veils ----
  // A veil name we would rather not print: empty, a generic word, or a
  // fragment (starts with a lower-case letter or has stray punctuation at
  // either end, which is what a short-name slip in the data looks like).
  // Shared by the veil ranking (skipped) and the bride captions (no
  // "Wearing" line), so a bad name never reaches the page.
  var GENERIC_VEIL_NAMES = { materials: 1, material: 1, veil: 1, veils: 1, 'wedding veil': 1,
    'bridal veil': 1, untitled: 1, unknown: 1, default: 1, test: 1 };
  function usableVeilName(name) {
    var n = String(name == null ? '' : name).trim();
    if (n.length < 2) return false;
    if (GENERIC_VEIL_NAMES[n.toLowerCase()]) return false;
    var first = n.charAt(0);
    var last = n.charAt(n.length - 1);
    if (!isWordChar(first) || !(isWordChar(last) || last === ')')) return false;
    if (isLetter(first) && first === first.toLowerCase()) return false;
    return true;
  }
  // Letters of any alphabet (Élise, Zoë) count; no Unicode regex, so an old browser cannot choke on the file.
  function isLetter(ch) { return ch.toLowerCase() !== ch.toUpperCase(); }
  function isWordChar(ch) { return isLetter(ch) || (ch >= '0' && ch <= '9'); }

  // Top four veils by matched-bride count. Counted here (not with
  // core.groupByVeil) so the count is not capped at a group's card limit.
  // Products that share a short name (two "Ada", two "Niacae") are ONE entry:
  // the count is the number of DIFFERENT matched brides wearing any of them
  // (a bride in both counts once, so "Worn by N of these brides" stays true);
  // the image is the best-scoring matched photo showing any of them (ranked
  // is best first, and a bride's photos are best first); "Shop the veil" and
  // the price follow the variant worn by the most matched brides (a tie goes
  // to the one seen first, i.e. worn by the closest bride).
  function tallyVeils(ranked) {
    var byName = {};
    var list = [];
    ranked.forEach(function (item, idx) {
      if (item.band == null) return;
      var brideKey = item.bride && item.bride.id != null ? String(item.bride.id) : 'r' + idx;
      var photos = (item.bride && item.bride.photos) || [];
      var seenVeil = {};
      photos.forEach(function (p) {
        if (!p || p.veil == null || seenVeil[p.veil]) return;
        seenVeil[p.veil] = true;
        var v = veilFor(p.veil);
        if (!v || !usableVeilName(v.name)) return;
        var key = String(v.name).trim().toLowerCase();
        var g = byName[key];
        if (!g) {
          g = { n: 0, photo: p, order: list.length, brides: {}, variants: {}, variantCount: 0 };
          byName[key] = g;
          list.push(g);
        }
        if (!g.brides[brideKey]) { g.brides[brideKey] = true; g.n += 1; }
        var variant = g.variants[p.veil];
        if (!variant) { variant = g.variants[p.veil] = { n: 0, order: g.variantCount++ }; }
        variant.n += 1;
      });
    });
    list.forEach(function (g) {
      var best = null;
      Object.keys(g.variants).forEach(function (id) {
        var c = g.variants[id];
        if (!best || c.n > best.n || (c.n === best.n && c.order < best.order)) best = { id: id, n: c.n, order: c.order };
      });
      g.id = best.id;
    });
    return list
      .sort(function (a, b) { return (b.n - a.n) || (a.order - b.order); })
      .slice(0, TOP_VEILS);
  }

  function fetchPrice(handle) {
    if (priceCache[handle]) return priceCache[handle];
    var url = ROOT_PREFIX + '/products/' + encodeURIComponent(handle) + '.js';
    priceCache[handle] = fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('product http ' + r.status);
      return r.json();
    }).then(function (p) {
      return formatMoney(p && (p.price_min != null ? p.price_min : p.price));
    }).catch(function () { return ''; });
    return priceCache[handle];
  }

  // Minor units (pence, cents) in the shopper's currency. '' when anything is off.
  function formatMoney(minor) {
    var cents = Number(minor);
    var currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (!isFinite(cents) || cents <= 0 || !currency) return '';
    var amount = cents / 100;
    var opts = { style: 'currency', currency: currency };
    if (Math.round(amount) === amount) { opts.minimumFractionDigits = 0; opts.maximumFractionDigits = 0; }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, opts).format(amount);
    } catch (e) { return ''; }
  }

  // Crop focus point (fp, marketing_131): [x, y] per cent across / down.
  // Returns an object-position value, or '' when fp is missing or malformed.
  function fpPos(p) {
    var f = p && p.fp;
    if (!f || !Array.isArray(f) || f.length !== 2) return '';
    var x = f[0], y = f[1];
    if (typeof x !== 'number' || typeof y !== 'number' || !isFinite(x) || !isFinite(y)) return '';
    if (x < 0 || x > 100 || y < 0 || y > 100) return '';
    return Math.round(x) + '% ' + Math.round(y) + '%';
  }

  function buildVeilCard(g, rank) {
    var veil = veilFor(g.id);
    var handle = handleOf(veil.url);
    var card = el('article', 'bdf-veil');

    var media = el('div', 'bdf-veil__media');
    var img = el('img');
    img.loading = 'lazy';
    img.decoding = 'async';
    img.src = cdnUrl(g.photo.src, 640);
    var veilPos = fpPos(g.photo);
    if (veilPos) img.style.objectPosition = veilPos;
    img.alt = safeI18n.t('js.rb.dm_photo_alt', { veil: veil.name });
    media.appendChild(img);
    media.appendChild(el('span', 'bdf-veil__rank', rank < 10 ? '0' + rank : String(rank)));
    card.appendChild(media);

    card.appendChild(el('h3', 'bdf-veil__name', veil.name));
    var type = [veil.lace, veil.fabric].filter(Boolean).join(' · ');
    if (type) card.appendChild(el('p', 'bdf-veil__type', type));

    var price = el('p', 'bdf-veil__price');
    card.appendChild(price);
    if (handle) {
      fetchPrice(handle).then(function (text) {
        if (text && price.isConnected) price.textContent = safeI18n.t('js.rb.df_price_from', { price: text });
      });
    }

    // "Worn by N" only when it says something (N of 1 is just this bride).
    if (g.n >= 2) {
      var worn = el('p', 'bdf-veil__worn');
      worn.appendChild(el('span', 'bdf-d', safeI18n.t('js.rb.df_worn_by', { n: g.n })));
      worn.appendChild(el('span', 'bdf-m', safeI18n.t('js.rb.df_worn_by_m', { n: g.n })));
      card.appendChild(worn);
    }

    var btns = el('div', 'bdf-veil__btns');
    if (veil.url) {
      var shop = el('a', 'bdf-btn bdf-btn--fill');
      shop.href = veil.url;
      shop.setAttribute('data-bdf-track', 'shop');
      shop.setAttribute('data-bdf-veil', veil.name);
      shop.appendChild(el('span', 'bdf-d', safeI18n.t('js.rb.dm_shop_the_veil')));
      shop.appendChild(el('span', 'bdf-m', safeI18n.t('js.rb.df_shop_m')));
      btns.appendChild(shop);

      // The main link keeps her dress: the archive reads ?dress=1 with her remembered
      // dress and shows the brides in a dress like hers who wore THIS veil.
      if (handle) {
        var seeDress = el('a', 'bdf-btn bdf-btn--line', safeI18n.t('js.rb.df_see_dress'));
        seeDress.href = ROOT_PREFIX + '/pages/real-brides?dress=1&product=' + encodeURIComponent(handle);
        seeDress.setAttribute('data-bdf-track', 'worn');
        seeDress.setAttribute('data-bdf-link', 'dress');
        seeDress.setAttribute('data-bdf-veil', veil.name);
        btns.appendChild(seeDress);
      }
    }
    card.appendChild(btns);

    // The small link is the plain product filter: every bride who wore the veil.
    // Both links fire finder_worn_click; data-bdf-link says which one was used.
    if (veil.url && handle) {
      var seeAll = el('a', 'bdf-veil__all', safeI18n.t('js.rb.df_see_veil_all', { veil: veil.name }));
      seeAll.href = ROOT_PREFIX + '/pages/real-brides?product=' + encodeURIComponent(handle);
      seeAll.setAttribute('data-bdf-track', 'worn');
      seeAll.setAttribute('data-bdf-link', 'all');
      seeAll.setAttribute('data-bdf-veil', veil.name);
      card.appendChild(seeAll);
    }
    return card;
  }

  function renderVeils(list, brideCount) {
    veilRowEl.textContent = '';
    if (!list.length) { veilsEl.hidden = true; return; }
    list.forEach(function (g, i) { veilRowEl.appendChild(buildVeilCard(g, i + 1)); });
    veilsMetaEl.textContent = safeI18n.n('js.rb.df_from_count', brideCount, { n: brideCount });
    veilsEl.hidden = false;
  }

  // ---- brides ----
  function buildBrideCard(item) {
    var b = item.bride;
    var photo = b && b.photos && b.photos[0];
    if (!photo) return null;
    var page = pageUrlOf(b);
    var veil = veilFor(photo.veil);

    var card = el(page ? 'a' : 'div', page ? 'brac brac--link' : 'brac');
    if (page) {
      card.href = page;
      card.setAttribute('data-bdf-track', 'bride');
      card.setAttribute('data-bdf-bride', b.id || '');
    }

    var media = el('span', 'brac__media');
    var img = el('img');
    img.loading = 'lazy';
    img.decoding = 'async';
    img.src = cdnUrl(photo.src, 520);
    var bridePos = fpPos(photo);
    if (bridePos) img.style.objectPosition = bridePos;
    var veilName = veil && usableVeilName(veil.name) ? veil.name : '';
    img.alt = veilName
      ? safeI18n.t('js.rb.dm_photo_alt', { veil: veilName })
      : safeI18n.t('js.rb.dm_photo_alt_no_veil');
    media.appendChild(img);
    if (item.band && BAND_LABEL[item.band]) {
      media.appendChild(el('span', 'brac__tag brac__tag--' + item.band, BAND_LABEL[item.band]));
    }
    card.appendChild(media);

    var title = el('span', 'brac__title');
    title.appendChild(el('span', 'brac__name', b.name || safeI18n.t('rb.bride_fallback_generic_name')));
    var roman = b.an ? toRoman(b.an) : '';
    if (roman) title.appendChild(el('span', 'brac__no', '№ ' + roman));
    card.appendChild(title);

    var sub = el('span', 'brac__sub');
    if (veilName) sub.appendChild(el('span', 'brac__wear', safeI18n.t('js.rb.df_wearing', { veil: veilName })));
    var place = b.vn || b.rg || '';
    if (place) sub.appendChild(el('span', 'brac__place', place));
    if (sub.firstChild) card.appendChild(sub);

    // "Same neckline · same sleeves": only the first one starts a sentence.
    var same = (item.shared || []).filter(function (f) { return f !== 'extras'; }).slice(0, 3).map(function (f, i) {
      var line = safeI18n.t('js.rb.dm_same_field', { field: (FIELD_LABEL[f] || f).toLowerCase() });
      return i ? line.charAt(0).toLowerCase() + line.slice(1) : line;
    });
    if (same.length) card.appendChild(el('span', 'brac__match', same.join(' · ')));
    return card;
  }

  function renderBrides(ranked) {
    brideGridEl.textContent = '';
    // Brides with a wedding page first, then the rest; core.closestFirst order
    // (closest first, a little variety in veils) within each group.
    var withPage = [];
    var without = [];
    core.closestFirst(ranked, {}).forEach(function (item) {
      (pageUrlOf(item.bride) ? withPage : without).push(item);
    });
    withPage.concat(without).slice(0, GRID_SIZE).forEach(function (item) {
      var card = buildBrideCard(item);
      if (card) brideGridEl.appendChild(card);
    });

    var n = ranked.length;
    bridesMetaEl.textContent = '';
    bridesMetaEl.appendChild(el('span', 'bdf-d', safeI18n.n('js.rb.df_brides_count', n, { n: n })));
    bridesMetaEl.appendChild(el('span', 'bdf-m', safeI18n.n('js.rb.df_brides_count_m', n, { n: n })));
    if (seeAllEl) seeAllEl.textContent = safeI18n.t('js.rb.df_see_all', { n: n });
    bridesEl.hidden = !brideGridEl.firstChild;
  }

  // ================================================================
  // Reset
  // ================================================================
  function reset() {
    S.gen += 1;
    core.cancel();
    stopReading();
    closePop();
    releasePreview();
    setThumb('');
    S.tags = null;
    S.ranked = null;
    S.recalled = false;
    S.lowCoverage = false;
    core.forget();
    fileEl.value = '';
    pickerSel = {};
    syncPicker();
    setPicker(false);
    clearResultsDom();
    labelEl.textContent = DEFAULT_LABEL;
    statusEl.textContent = '';
    showStart('');
    core.emit('finder_reset', {});
    if (!S.unavailable) dropEl.focus();
  }

  // ================================================================
  // Wiring
  // ================================================================
  function wire() {
    fileEl.addEventListener('change', function () {
      var f = fileEl.files && fileEl.files[0];
      fileEl.value = '';
      if (f) handleFile(f);
    });
    dropEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileEl.click(); }
    });
    ['dragenter', 'dragover'].forEach(function (evt) {
      dropEl.addEventListener(evt, function (e) { e.preventDefault(); dropEl.classList.add('bdf-drop--over'); });
    });
    ['dragleave', 'drop'].forEach(function (evt) {
      dropEl.addEventListener(evt, function (e) { e.preventDefault(); dropEl.classList.remove('bdf-drop--over'); });
    });
    dropEl.addEventListener('drop', function (e) {
      var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) handleFile(f);
    });

    describeBtn.addEventListener('click', function () {
      var open = pickerEl.hidden;
      setPicker(open);
      if (open) {
        core.emit('finder_describe_open', {});
        if (!S.unavailable) ensureEngine(); // so Find can tell whether her details are enough
      }
    });
    optBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        var field = b.getAttribute('data-bdf-field');
        var value = b.getAttribute('data-bdf-value');
        if (pickerSel[field] === value) delete pickerSel[field];
        else pickerSel[field] = value;
        syncPicker();
      });
    });
    findBtn.addEventListener('click', findFromPicker);
    resetBtn.addEventListener('click', reset);

    // Tracking for the links the JS builds (delegated: cards are rebuilt on every edit).
    root.addEventListener('click', function (e) {
      var t = e.target;
      var node = t && t.closest ? t.closest('[data-bdf-track]') : null;
      if (!node) return;
      var kind = node.getAttribute('data-bdf-track');
      core.emit('finder_' + kind + '_click', {
        veil: node.getAttribute('data-bdf-veil') || '',
        bride: node.getAttribute('data-bdf-bride') || '',
        // finder_worn_click only: 'dress' (with dresses like hers) or 'all' (every bride in it).
        link: node.getAttribute('data-bdf-link') || ''
      });
      // "Shop the veil": the cart remembers the finder as where she started (first touch wins).
      // assets/britten-rb-session.js; a missing helper or a blocked write never delays the link.
      if (kind === 'shop') {
        try { if (window.BrittenRb) window.BrittenRb.touch('dress_finder'); } catch (err) { /* never break the click */ }
      }
    });
    if (seeAllEl) {
      seeAllEl.addEventListener('click', function () {
        core.emit('finder_see_all', { brides: S.ranked ? S.ranked.length : 0 });
      });
    }
  }

  // ================================================================
  // Boot
  // ================================================================
  function boot() {
    var Core = window.BrittenDressMatchCore;
    if (!Core) {
      // The engine file is missing: a calm line, never a dead upload box.
      core = { revoke: function () {}, cancel: function () {}, forget: function () {}, emit: function () {} };
      markUnavailable({ code: 'no_score' });
      return;
    }
    core = Core.create({
      indexUrl: root.getAttribute('data-index-url') || '',
      scoreUrl: root.getAttribute('data-score-url') || '',
      endpoint: root.getAttribute('data-endpoint') || undefined,
      turnstileSitekey: root.getAttribute('data-turnstile-sitekey') || '',
      debug: DEBUG_MODE
    });

    wire();
    syncPicker();

    // Launch safety: a cheap existence check for the bride index. Missing =
    // a calm line instead of an upload box that cannot go anywhere.
    core.probeIndex().then(function (ok) {
      if (!ok) { if (!S.engine) markUnavailable({ code: 'no_index' }); return; }
      // Warm the engine now, so results are instant. If it cannot load, say so
      // before she uploads anything (mid-upload failures are handled by showResults).
      idle(function () {
        ensureEngine().catch(function (err) { if (S.state === 'start') markUnavailable(err); });
      });
    });

    // A dress remembered this session: straight to the results.
    var recalled = core.recall();
    if (recalled && hasKeys(recalled.tags)) {
      S.gen += 1;
      S.tags = recalled.tags;
      S.recalled = true;
      labelEl.textContent = safeI18n.t('js.rb.df_last_dress');
      setThumb('');
      setState('reading');
      startReading();
      showResults(S.gen);
    }
  }

  boot();
}());
